import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import { Container } from '../components/ui/Layout';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { FiPlus, FiEdit2, FiTrash2, FiMail, FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import SubUserModal from '../components/sub-users/SubUserModal';
import apiClient from '../api/apiClient';

const SubUsers = () => {
  const [subUsers, setSubUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSubUser, setSelectedSubUser] = useState(null);
  const [permissionTemplates, setPermissionTemplates] = useState({});

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [subUsersResponse, projectsResponse, templatesResponse] = await Promise.all([
        apiClient.get('/sub-users'),
        apiClient.get('/projects'),
        apiClient.get('/sub-users/permission-templates')
      ]);

      console.log('Sub users response:', subUsersResponse.data);
      console.log('Projects response:', projectsResponse.data);
      console.log('Templates response:', templatesResponse.data);

      setSubUsers(subUsersResponse.data?.subUsers || []);
      setProjects(projectsResponse.data?.projects || []);
      setPermissionTemplates(templatesResponse.data?.templates || {});
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load sub users');
      // Ensure we set empty arrays even on error
      setSubUsers([]);
      setProjects([]);
      setPermissionTemplates({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSubUser = () => {
    setSelectedSubUser(null);
    setShowModal(true);
  };

  const handleEditSubUser = (subUser) => {
    setSelectedSubUser(subUser);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSubUser(null);
  };

  const handleSaveSubUser = async (subUserData) => {
    try {
      if (selectedSubUser) {
        // Update existing sub user
        await apiClient.patch(`/sub-users/${selectedSubUser._id}`, subUserData);
        toast.success('Sub user updated successfully');
      } else {
        // Create new sub user
        await apiClient.post('/sub-users', subUserData);
        toast.success('Sub user created and invitation sent');
      }
      
      handleCloseModal();
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error saving sub user:', error);
      toast.error(error.response?.data?.message || 'Failed to save sub user');
    }
  };

  const handleDeleteSubUser = async (subUser) => {
    if (!window.confirm(`Are you sure you want to delete ${subUser.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiClient.delete(`/sub-users/${subUser._id}`);
      toast.success('Sub user deleted successfully');
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error deleting sub user:', error);
      toast.error('Failed to delete sub user');
    }
  };

  const handleResendInvitation = async (subUser) => {
    try {
      await apiClient.post(`/sub-users/${subUser._id}/resend-invitation`);
      toast.success('Invitation email sent successfully');
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error(error.response?.data?.message || 'Failed to send invitation');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionSummary = (permissions) => {
    const truePermissions = Object.entries(permissions).filter(([_, value]) => value).length;
    const totalPermissions = Object.keys(permissions).length;
    return `${truePermissions}/${totalPermissions} permissions`;
  };

  if (isLoading) {
    return (
      <Layout>
        <Container className="py-8">
          <div className="flex justify-center items-center h-64">
            <Spinner size="lg" />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container className="py-8">
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Sub Users</h1>
              <p className="text-gray-600">Manage team members with limited access to your projects</p>
            </div>
            <Button
              onClick={handleCreateSubUser}
              className="btn btn-primary"
            >
              <FiPlus className="mr-2" />
              Add Sub User
            </Button>
          </div>
        </div>

        {!Array.isArray(subUsers) || subUsers.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="max-w-md mx-auto">
                <FiPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No sub users yet</h3>
                <p className="text-gray-500 mb-6">
                  Create sub user accounts to give team members limited access to your projects.
                </p>
                <Button
                  onClick={handleCreateSubUser}
                  className="btn btn-primary"
                >
                  Create Your First Sub User
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {Array.isArray(subUsers) && subUsers.map((subUser) => (
              <Card key={subUser._id} className="shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-lg font-medium text-gray-900 mr-3">
                          {subUser.name}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${
                          subUser.emailVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {subUser.emailVerified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{subUser.email}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Projects Access</p>
                          <p className="text-sm font-medium">
                            {subUser.accessibleProjects.length} project{subUser.accessibleProjects.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Last Login</p>
                          <p className="text-sm font-medium">{formatDate(subUser.lastLogin)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Created</p>
                          <p className="text-sm font-medium">{formatDate(subUser.createdAt)}</p>
                        </div>
                      </div>

                      {/* Project Access Summary */}
                      {subUser.accessibleProjects.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-gray-500 mb-2">Project Access:</p>
                          <div className="flex flex-wrap gap-2">
                            {subUser.accessibleProjects.map((access) => (
                              <div key={access.project._id} className="bg-gray-100 rounded-lg p-2 text-xs">
                                <div className="font-medium">{access.project.name}</div>
                                <div className="text-gray-600">{getPermissionSummary(access.permissions)}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      {!subUser.emailVerified && (
                        <Button
                          onClick={() => handleResendInvitation(subUser)}
                          variant="outline"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <FiMail className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        onClick={() => handleEditSubUser(subUser)}
                        variant="outline"
                        size="sm"
                        className="text-gray-600 hover:text-gray-700"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteSubUser(subUser)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Sub User Modal */}
        {showModal && (
          <SubUserModal
            subUser={selectedSubUser}
            projects={projects}
            permissionTemplates={permissionTemplates}
            onSave={handleSaveSubUser}
            onClose={handleCloseModal}
          />
        )}
      </Container>
    </Layout>
  );
};

export default SubUsers; 